import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Empty, List, Message, Modal, Pagination, Tooltip } from '@arco-design/web-react';
import { IconCloseCircle, IconExclamationCircle, IconInfoCircle } from '@arco-design/web-react/icon';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { RenderIcon, dateFormatRender } from '@/components';
import { useAuthFun } from '@/hooks/useAuth';
import { getModule } from '@/hooks/useRouteScope';
import { SEVERITY_OPTS, TYPE_OPTS } from '@/pages/system/ops-center/notices/const';
import { GROUPS } from '@/service/groups';
import { getAccessToken } from '@/utils/useLocalStorage';

import './index.css';
import { UrlMap, getList, read, readAll } from './service';

const ListItem = styled(List.Item)`
  .arco-list-item-content {
    display: flex;
    align-items: center;
  }
  .arco-list-item-main {
    width: 100%;
    min-width: 0;
    overflow: visible;
  }
`;

const CustomModal = styled(Modal)`
  .arco-modal-content {
    padding: 0;
    overflow: hidden;
  }
`;

export interface NotificationItem {
  datacenter: string;
  workspace: string;
  severity: 'Info' | 'Warning' | 'Critical';
  type: string;
  title: string;
  content: string;
  sourceEntity: string;
  sourceID: string;
  creatorType: string;
  creatorID: string;
  creatorName: string;
  Inserted: boolean;
  isRead: boolean;
  createTime: string;
  uuid: string;
  readerName?: string;
}

// 产品类型权限
export const useAuth = () => {
  const getAuth = useAuthFun(GROUPS.notice);
  const [auth, setAuth] = useState<any>({});

  useEffect(() => {
    cpuAuth();
  }, [getAuth]);

  const cpuAuth = async () => {
    const auth = {
      watch: await getAuth('watch', 'list'), // 监听
      notices: await getAuth('notices', 'list'), // 列表
      read: await getAuth('read', 'put'), // 标记为已读
      readAll: await getAuth('readall', 'put'), // 标记为已读所有
    };
    setAuth(auth);
  };

  return auth;
};

const LIMIT = 5;
// 通知严重程度对应的语义图标
const SEVERITY_ICONS = {
  Info: IconInfoCircle,
  Warning: IconExclamationCircle,
  Critical: IconCloseCircle,
};

const Notification: React.FC = () => {
  const { t } = useTranslation();
  const module = getModule();
  const params = useParams();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [notificationList, setNotificationList] = useState<NotificationItem[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readAllSubmitting, setReadAllSubmitting] = useState(false);
  const auth = useAuth();

  // 获取消息列表
  const getNotices = async (p?: number) => {
    const currentPage = p || page;

    // 如果没有更多数据，则不请求
    try {
      if (!auth.notices) return;
      setLoading(true);
      let { items, unread } = await getList({
        page: currentPage,
        limit: LIMIT,
        sortBy: 'createTime',
        order: 'desc',
      });

      items = items || [];
      unread = unread || 0;

      setNotificationList(items);
      setPage(currentPage);
      setUnreadCount(unread);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  // 长链接接收数据 - 刷新第一页
  const watchNotice = useCallback(() => {
    if (!auth.watch) return;
    const xhr = new XMLHttpRequest();
    const url = UrlMap().watch;
    xhr.open('GET', url, true);

    xhr.setRequestHeader('Authorization', `Bearer ${getAccessToken()}`);
    xhr.setRequestHeader('X-Operation-ID', uuidv4());

    xhr.onreadystatechange = () => {
      if (xhr.readyState >= 3 && xhr.status === 200) {
        getNotices();
      }
    };

    xhr.send();
    return xhr;
  }, [auth.watch]);

  // 初始化
  useEffect(() => {
    getNotices(1);
    let xhr: XMLHttpRequest | null = null;
    const clear = () => {
      if (xhr) {
        xhr.abort(); // 组件卸载时中止请求
      }
    };
    clear();
    const startWatch = () => {
      xhr = watchNotice();
    };
    startWatch();
    return clear;
  }, [watchNotice, module, params.tenant, params.cluster]);

  // 点击外部关闭面板
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(event.target as Node)
      ) {
        setIsPanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 点击浮动按钮
  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen);
  };

  // 关闭消息框
  const handleClosePanel = () => {
    setIsPanelOpen(false);
  };

  // 标记为已读 - 更新
  const handleNotificationClick = async (item: NotificationItem) => {
    try {
      if (item.isRead) return;
      await read(item.uuid);
      getNotices();
    } catch (e) {
      console.log(e);
    }
  };

  // 标记为已读所有（一键清除未读）
  const handleReadAll = async () => {
    if (unreadCount <= 0 || readAllSubmitting) return;
    try {
      setReadAllSubmitting(true);
      await readAll();
      await getNotices(1);
      Message.success(t('components:all.notifications.marked.read'));
    } catch (e) {
      console.log(e);
    } finally {
      setReadAllSubmitting(false);
    }
  };

  const total = notificationList?.length < LIMIT ? page * LIMIT : page * LIMIT + 1;

  if (!auth.notices || !auth.watch) return null;

  return (
    <div className="notification-container">
      <div ref={bellRef} className="notification-bell" onClick={togglePanel}>
        <RenderIcon style={{ margin: 0 }} Icon="icon-a-22xiaoxitongzhi" />
        {unreadCount > 0 && (
          <div className="notification-count">
            <span className="notification-count-text">{unreadCount > 99 ? '99+' : unreadCount}</span>
          </div>
        )}
      </div>

      <CustomModal
        style={{ width: 760, maxWidth: 'calc(100vw - 32px)' }}
        visible={isPanelOpen}
        title={
          <div className="notification-modal-custom-title">
            <span className="notification-modal-heading">{t('common:notification.unread', { count: unreadCount || 0 })}</span>
            {auth.readAll && unreadCount > 0 ? (
              <span className="notification-read-all-btn" onClick={handleReadAll}>
                {t('components:mark.all.notifications.read')}
              </span>
            ) : null}
          </div>
        }
        onCancel={handleClosePanel}
        onOk={handleClosePanel}
        footer={null}
      >
        <List
          className="notification-list"
          loading={loading}
          scrollLoading={!hasMore && notificationList.length > 0 ? t('components:no.more.notifications') : undefined}
          dataSource={notificationList}
          render={(notification) => {
            const type = TYPE_OPTS.find((item) => item.value === notification.type)?.label || notification.type;
            const severity = SEVERITY_OPTS.find((item) => item.value === notification.severity)?.label || notification.severity;
            const SeverityIcon = SEVERITY_ICONS[notification.severity];
            return (
              <ListItem
                key={`${notification.uuid}-${notification.isRead}`}
                className={`notification-item ${notification.isRead ? '' : 'unread'}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <Tooltip content={severity} position="top" triggerProps={{ alignPoint: true }}>
                  <div className={`notification-icon ${notification.severity.toLowerCase()}`}>
                    <SeverityIcon />
                    {!notification.isRead && <span className="notification-unread-dot" />}
                  </div>
                </Tooltip>
                <div className="notification-content">
                  <div className="notification-message">
                    <div className="notification-title-row">
                      <span className="notification-item-title">{notification.title}</span>
                      <span className="notification-creator-type">{type}</span>
                    </div>
                    <span className="notification-time">{dateFormatRender(notification.createTime)}</span>
                  </div>
                  <div className="notification-item-footer">
                    <div className="notification-item-content-wrap">
                      <Tooltip content={notification.content} position="top" triggerProps={{ alignPoint: true }}>
                        <span className="notification-item-content">{notification.content}</span>
                      </Tooltip>
                    </div>
                    <div className="notification-read-meta">
                      {notification.isRead && notification.readerName ? (
                        <span className="notification-reader">{t('common:recipient.display', { name: notification.readerName })}</span>
                      ) : null}
                      <span className={`notification-read-status ${notification.isRead ? 'read' : 'unread'}`}>
                        {notification.isRead ? t('components:read') : t('components:unread')}
                      </span>
                    </div>
                  </div>
                </div>
              </ListItem>
            );
          }}
          noDataElement={<Empty />}
        />
        <div className="notification-pagination">
          <Pagination
            current={page}
            pageSize={LIMIT}
            total={total}
            onChange={(p) => {
              getNotices(p);
            }}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default Notification;
